import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js'
import { Bar, Pie } from 'react-chartjs-2'
import {
  Box,
  CircularProgress,
  Typography,
  useTheme,
  Divider,
  Chip,
} from '@mui/material'
import supabase from '../supabaseClient'
import dayjs from 'dayjs'
import TimePeriodFilter from './ui/TimePeriodFilter'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
)

const StudentAttendanceChart = ({ studentId }) => {
  const [attendanceData, setAttendanceData] = useState({
    Present: 0,
    Absent: 0,
    'Excused Absence': 0,
    Late: 0,
  })
  const [detailedData, setDetailedData] = useState([])
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState('month')
  const [selectedStatus, setSelectedStatus] = useState(null)
  const theme = useTheme()

  // Handle view mode change
  const handleViewModeChange = (event, newValue) => {
    if (newValue !== null) {
      setViewMode(newValue)
      setSelectedStatus(null) // Reset selected status when changing view mode
    }
  }

  // Handle bar click to show detailed breakdown - wrap in useCallback
  const handleBarClick = useCallback((event, elements) => {
    if (elements.length > 0) {
      const { index } = elements[0]
      const statuses = ['Absences (Total)', 'Late']
      setSelectedStatus(statuses[index])
    }
  }, []) // No dependencies needed as statuses is a constant array

  // Fetch attendance data
  useEffect(() => {
    const fetchAttendanceData = async () => {
      if (!studentId) return

      try {
        setLoading(true)

        // Get current date
        const now = dayjs()
        let startDate

        // Calculate start date based on view mode
        switch (viewMode) {
          case 'week':
            startDate = now.subtract(7, 'day').format('YYYY-MM-DD')
            break
          case 'month':
            startDate = now.subtract(30, 'day').format('YYYY-MM-DD')
            break
          case '6months':
            startDate = now.subtract(180, 'day').format('YYYY-MM-DD')
            break
          case 'year':
            startDate = now.subtract(365, 'day').format('YYYY-MM-DD')
            break
          default:
            startDate = now.subtract(30, 'day').format('YYYY-MM-DD')
        }

        // Query for this specific student's attendance with class information
        const { data, error } = await supabase
          .from('attendance')
          .select(
            `
            status, 
            date,
            class_id,
            classes(name)
          `,
          )
          .eq('student_id', studentId)
          .gte('date', startDate)

        if (error) {
          console.error('Error fetching student attendance data:', error)
          return
        }

        // Count the different status types
        const counts = {
          Present: 0,
          Absent: 0,
          'Excused Absence': 0,
          Late: 0,
        }

        data.forEach((record) => {
          if (counts.hasOwnProperty(record.status)) {
            counts[record.status]++
          }
        })

        setAttendanceData(counts)
        setDetailedData(data)
      } catch (error) {
        console.error('Error in fetchAttendanceData:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchAttendanceData()
  }, [studentId, viewMode])

  // Chart data for the main bar chart
  const chartData = useMemo(
    () => ({
      labels: ['Absences (Total)', 'Late'],
      datasets: [
        {
          label: 'Attendance',
          data: [attendanceData.Absent + attendanceData['Excused Absence'], attendanceData.Late],
          backgroundColor: [
            'rgba(255, 99, 132, 0.2)', // Red for Absent
            'rgba(255, 205, 86, 0.2)', // Yellow for Late
          ],
          borderColor: [
            'rgb(255, 99, 132)', // Red for Absent
            'rgb(255, 205, 86)', // Yellow for Late
          ],
          borderWidth: 1,
        },
      ],
    }),
    [attendanceData],
  )

  // Chart options for the main bar chart
  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      onClick: handleBarClick,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            color: theme.palette.text.primary,
            font: {
              family: theme.typography.fontFamily,
              size: 12,
            },
          },
        },
        title: {
          display: true,
          text: `Attendance Overview (${viewMode.charAt(0).toUpperCase() + viewMode.slice(1)})`,
          color: theme.palette.text.primary,
          font: {
            family: theme.typography.fontFamily,
            size: 16,
            weight: 'bold',
          },
        },
        tooltip: {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(0, 0, 0, 0.8)'
              : 'rgba(255, 255, 255, 0.8)',
          titleColor:
            theme.palette.mode === 'dark'
              ? theme.palette.text.primary
              : theme.palette.text.primary,
          bodyColor:
            theme.palette.mode === 'dark'
              ? theme.palette.text.primary
              : theme.palette.text.primary,
          borderColor: theme.palette.divider,
          borderWidth: 1,
        },
      },
      scales: {
        x: {
          grid: {
            color: theme.palette.divider,
          },
          ticks: {
            color: theme.palette.text.secondary,
          },
        },
        y: {
          grid: {
            color: theme.palette.divider,
          },
          ticks: {
            color: theme.palette.text.secondary,
          },
        },
      },
    }),
    [viewMode, theme, handleBarClick],
  )

  // Prepare data for the pie chart (class breakdown)
  const pieChartData = useMemo(() => {
    if (!selectedStatus) return null

    if (selectedStatus === 'Absences (Total)') {
      // Show breakdown of Absent vs Excused Absence
      const absentCount = attendanceData.Absent;
      const excusedCount = attendanceData['Excused Absence'];
      
      if (absentCount === 0 && excusedCount === 0) {
        return null;
      }

      return {
        labels: ['Unexcused Absences', 'Excused Absences'],
        datasets: [
          {
            data: [absentCount, excusedCount],
            backgroundColor: [
              'rgba(255, 99, 132, 0.2)', // Red for unexcused (matching bar graph)
              'rgba(76, 175, 80, 0.2)', // Green for excused (matching bar graph)
            ],
            borderColor: [
              'rgb(255, 99, 132)', // Red for unexcused (matching bar graph)
              'rgb(76, 175, 80)', // Green for excused (matching bar graph)
            ],
            borderWidth: 1,
          },
        ],
      };
    } else {
      // For Late status, show breakdown by class (existing logic)
      const filteredRecords = detailedData.filter(
        (record) => record.status === selectedStatus,
      )

      // Group by class name
      const classCounts = {}
      filteredRecords.forEach((record) => {
        const className = record.classes?.name || `Class ID: ${record.class_id}`
        classCounts[className] = (classCounts[className] || 0) + 1
      })

      // Use consistent color palette
      const backgroundColor = [
        'rgba(255, 99, 132, 0.2)',
        'rgba(255, 159, 64, 0.2)',
        'rgba(255, 205, 86, 0.2)',
        'rgba(75, 192, 192, 0.2)',
        'rgba(54, 162, 235, 0.2)',
        'rgba(153, 102, 255, 0.2)',
        'rgba(201, 203, 207, 0.2)',
      ]

      const borderColor = [
        'rgb(255, 99, 132)',
        'rgb(255, 159, 64)',
        'rgb(255, 205, 86)',
        'rgb(75, 192, 192)',
        'rgb(54, 162, 235)',
        'rgb(153, 102, 255)',
        'rgb(201, 203, 207)',
      ]

      // If we have more classes than colors, repeat the colors
      const classCount = Object.keys(classCounts).length
      const extendedBackgroundColors = Array(classCount)
        .fill()
        .map((_, i) => backgroundColor[i % backgroundColor.length])

      const extendedBorderColors = Array(classCount)
        .fill()
        .map((_, i) => borderColor[i % borderColor.length])

      return {
        labels: Object.keys(classCounts),
        datasets: [
          {
            data: Object.values(classCounts),
            backgroundColor: extendedBackgroundColors,
            borderColor: extendedBorderColors,
            borderWidth: 1,
          },
        ],
      }
    }
  }, [selectedStatus, detailedData, attendanceData])

  // Pie chart options
  const pieChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
          labels: {
            color: theme.palette.text.primary,
            font: {
              family: theme.typography.fontFamily,
              size: 12,
            },
          },
        },
        title: {
          display: true,
          text: selectedStatus === 'Absences (Total)' 
            ? 'Absence Breakdown (Unexcused vs Excused)'
            : `${selectedStatus} Breakdown by Class`,
          color: theme.palette.text.primary,
          font: {
            family: theme.typography.fontFamily,
            size: 16,
            weight: 'bold',
          },
        },
        tooltip: {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(0, 0, 0, 0.8)'
              : 'rgba(255, 255, 255, 0.8)',
          titleColor:
            theme.palette.mode === 'dark'
              ? theme.palette.text.primary
              : theme.palette.text.primary,
          bodyColor:
            theme.palette.mode === 'dark'
              ? theme.palette.text.primary
              : theme.palette.text.primary,
          borderColor: theme.palette.divider,
          borderWidth: 1,
        },
      },
    }),
    [selectedStatus, theme],
  )

  // Chart container style
  const chartContainerStyle = {
    height: 300,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
  }

  // If no attendance data is found
  const totalAttendance =
    attendanceData.Present + attendanceData.Absent + attendanceData['Excused Absence'] + attendanceData.Late

  if (loading) {
    return (
      <Box>
        <TimePeriodFilter value={viewMode} onChange={handleViewModeChange} />
        <Box sx={chartContainerStyle}>
          <CircularProgress size={40} thickness={4} />
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <TimePeriodFilter value={viewMode} onChange={handleViewModeChange} />

      {totalAttendance === 0 ? (
        <Box sx={chartContainerStyle}>
          <Typography variant="body1" color="text.secondary">
            No attendance data available for the selected period.
          </Typography>
        </Box>
      ) : (
        <>

          <Box sx={chartContainerStyle}>
            <Bar options={chartOptions} data={chartData} />
          </Box>

          {selectedStatus && pieChartData && (
        <>
          <Box sx={{ mt: 2, mb: 2 }}>
            <Divider>
              <Typography variant="body2" color="text.secondary">
                Click on a bar above to see class breakdown
              </Typography>
            </Divider>
          </Box>

          <Box sx={chartContainerStyle}>
            <Pie options={pieChartOptions} data={pieChartData} />
          </Box>

          {/* Date list for Absences (Total) or Late statuses */}
          {(selectedStatus === 'Absences (Total)' || selectedStatus === 'Late') && (
            <Box sx={{ mt: 3, px: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {selectedStatus === 'Absences (Total)' 
                  ? 'Dates when student was absent:'
                  : `Dates when student was ${selectedStatus.toLowerCase()}:`
                }
              </Typography>
              <Box
                sx={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: theme.shape.borderRadius,
                  p: 2,
                }}
              >
                {detailedData
                  .filter((record) => 
                    selectedStatus === 'Absences (Total)' 
                      ? (record.status === 'Absent' || record.status === 'Excused Absence')
                      : record.status === selectedStatus
                  )
                  .sort((a, b) => new Date(b.date) - new Date(a.date)) // Sort by date, newest first
                  .map((record, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 0.5,
                        borderBottom:
                          index <
                          detailedData.filter((r) => 
                            selectedStatus === 'Absences (Total)' 
                              ? (r.status === 'Absent' || r.status === 'Excused Absence')
                              : r.status === selectedStatus
                          ).length - 1
                            ? `1px solid ${theme.palette.divider}`
                            : 'none',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                          {dayjs(record.date).format('MMM D, YYYY')} (
                          {dayjs(record.date).format('dddd')})
                        </Typography>
                        {selectedStatus === 'Absences (Total)' && (
                          <Chip
                            label={record.status}
                            size="small"
                            color={record.status === 'Excused Absence' ? 'success' : 'error'}
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {record.classes?.name || `Class ID: ${record.class_id}`}
                      </Typography>
                    </Box>
                  ))}
                {detailedData.filter((record) => 
                  selectedStatus === 'Absences (Total)' 
                    ? (record.status === 'Absent' || record.status === 'Excused Absence')
                    : record.status === selectedStatus
                ).length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: 'center', py: 1 }}
                  >
                    No detailed records found.
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </>
      )}

          {!selectedStatus && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Click on a bar above to see class breakdown
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

export default StudentAttendanceChart
